import { BRAND_SITE_URL, BRAND_SITE_HOST } from "@/lib/brandConfig";
import { useTranslation } from "react-i18next";
import { resolveField, useResolvedCtas } from "@/hooks/useResolvedCtas";
import { useTemplateSelection } from "@/hooks/useTemplateSelection";
import { trackButtonClickGA, trackWhatsAppBroadcastClickGA } from "@/lib/analytics";
import { trackButtonClickPostHog, trackWhatsAppBroadcastPostHog } from "@/lib/posthogAnalytics";
import { trackInitiateCheckoutMeta, trackWhatsAppBroadcastMeta } from "@/lib/metaPixelAnalytics";

const WHATSAPP_BROADCAST_URL = "/whatsapp-broadcast";
const OTHER_COUNTRIES_STRIPE_URL =
  "https://buy.stripe.com/bJe3cv1RI7n39KL1VZ5wI3F";

export const MaintenanceTemplate: React.FC = () => {
  const { t } = useTranslation();
  const { country, whatsappBroadcast } = useTemplateSelection();
  const { ctas } = useResolvedCtas();
  // Checkout "otros países": override desde admin (/admin/cta) > link estático.
  const stripeCheckoutUrl = resolveField(
    ctas.telegram_basic,
    "url",
    OTHER_COUNTRIES_STRIPE_URL,
  );
  // Argentina: solo mostrar WhatsApp si el worker lo configuró (provincia en lista); nunca Stripe
  const shouldShowWhatsApp = country === "AR" && !!whatsappBroadcast;
  const shouldShowStripe = country !== "AR";

  const onWhatsAppClick = () => {
    trackWhatsAppBroadcastClickGA();
    trackWhatsAppBroadcastMeta();
    trackWhatsAppBroadcastPostHog();
  };

  const onStripeClick = () => {
    trackButtonClickGA(
      t("maintenance.stripeCta"),
      "maintenance_stripe_access",
      "payment_redirect",
      "maintenance_access",
      country || "UNKNOWN",
      "MaintenanceTemplate",
    );
    trackButtonClickPostHog(
      t("maintenance.stripeCta"),
      "maintenance_stripe_access",
      "payment_redirect",
      "maintenance_access",
      country || "UNKNOWN",
      "MaintenanceTemplate",
    );
    trackInitiateCheckoutMeta({
      content_ids: ["maintenance_template_access"],
      currency: "USD",
      num_items: 1,
    });
  };

  return (
    <main className="min-h-screen bg-black text-white flex items-center justify-center px-6">
      <section className="w-full max-w-xl rounded-2xl border border-white/20 bg-white/5 p-8 text-center">
        <p className="inline-flex items-center rounded-full border border-white/30 px-4 py-1 text-xs uppercase tracking-[0.22em] text-white/80">
          {t("maintenance.badge", { year: new Date().getFullYear() })}
        </p>
        <h1 className="mt-6 text-3xl sm:text-4xl font-semibold">{t("maintenance.title")}</h1>
        <p className="mt-4 text-base sm:text-lg text-white/80">{t("maintenance.message")}</p>
        {shouldShowWhatsApp && (
          <a
            href={WHATSAPP_BROADCAST_URL}
            onClick={onWhatsAppClick}
            className="mt-6 inline-flex items-center gap-3 py-3.5 px-5 bg-white/[0.08] backdrop-blur-[16px] border border-white/[0.15] rounded-[14px] hover:bg-white/[0.12] hover:border-white/[0.25] transition-all duration-200 max-w-[360px]"
          >
            <svg
              className="w-6 h-6 flex-shrink-0 text-[#25D366]"
              viewBox="0 0 24 24"
              fill="currentColor"
            >
              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
            </svg>
            <div className="flex flex-col gap-0.5 min-w-0 text-left">
              <span className="text-[15px] font-semibold text-white leading-tight">
                {t("maintenance.whatsappCta")}
              </span>
              <span className="text-[13px] font-normal text-white/60 leading-tight">
                {t("maintenance.whatsappHint")}
              </span>
            </div>
          </a>
        )}
        {shouldShowStripe && (
          <a
            href={stripeCheckoutUrl}
            target="_blank"
            rel="noopener noreferrer"
            onClick={onStripeClick}
            className="mt-6 inline-flex items-center gap-3 py-3.5 px-5 bg-white/[0.08] backdrop-blur-[16px] border border-white/[0.15] rounded-[14px] hover:bg-white/[0.12] hover:border-white/[0.25] transition-all duration-200 max-w-[360px]"
          >
            <span className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-white/15 text-sm font-semibold text-white">
              S
            </span>
            <div className="flex flex-col gap-0.5 min-w-0 text-left">
              <span className="text-[15px] font-semibold text-white leading-tight">
                {t("maintenance.stripeCta")}
              </span>
              <span className="text-[13px] font-normal text-white/60 leading-tight">
                {t("maintenance.stripeHint")}
              </span>
            </div>
          </a>
        )}
        <p className="mt-6 text-sm text-white/60">{t("maintenance.thanks")}</p>
        <footer className="mt-6 border-t border-white/10 pt-5">
          <a
            href={BRAND_SITE_URL}
            className="text-white/60 hover:text-white/80 transition-colors text-sm"
          >
            {BRAND_SITE_HOST}
          </a>
        </footer>
      </section>
    </main>
  );
};

export default MaintenanceTemplate;
